import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Science as ScienceIcon,
  Medication as MedicationIcon,
  Restaurant as NutritionIcon,
  ElectricBolt as SeizuresIcon,
  Vaccines as VaccinationIcon,
  AccountCircle as AccountIcon,
  Pets as PetsIcon,
  Info as AboutIcon,
  Email as FeedbackIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import AppStyles from '../styles/AppStyles';
import { useQueryClient } from '@tanstack/react-query';

const drawerWidth = 240;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function Layout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const menuItems = [
    { text: t('titles.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('titles.labResults'), icon: <ScienceIcon />, path: '/lab-results' },
    { text: t('titles.medication'), icon: <MedicationIcon />, path: '/medication' },
    { text: t('titles.nutrition'), icon: <NutritionIcon />, path: '/nutrition' },
    { text: t('titles.seizures'), icon: <SeizuresIcon />, path: '/seizures' },
    { text: t('titles.vaccination'), icon: <VaccinationIcon />, path: '/vaccination' },
  ];
  const queryClient = useQueryClient();
  const [patients, setPatients] = useState([]);
  const [currentPetId, setCurrentPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const [currentPetName, setCurrentPetName] = useState(() => localStorage.getItem('currentPetName') || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const forceInvalidateOnExternalChange = () => {
      queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
    };
    window.addEventListener('storage', forceInvalidateOnExternalChange);
    window.addEventListener('petChanged', forceInvalidateOnExternalChange);
    return () => {
      window.removeEventListener('storage', forceInvalidateOnExternalChange);
      window.removeEventListener('petChanged', forceInvalidateOnExternalChange);
    };
  }, [queryClient]);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('vetToken');
        const response = await fetch(`${API_BASE}/api/sharing/me/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const response_data = await response.json();
        console.log('Fetched patients:', response_data);
        const patientsList = Array.isArray(response_data.data) ? response_data.data : [];
        
        // Transform the data to match our expected format
        const transformedPatients = patientsList.map(p => ({
          id: p.petId,
          name: p.petName,
          species: p.petSpecies,
          ownerName: p.ownerName || null,
          ownerHasActiveSubscription: p.ownerHasActiveSubscription === true // Only true if explicitly true, default to false for safety
        }));
        
        setPatients(transformedPatients);
        
        // Only redirect to no-pets if there are truly NO pets at all
        // If there are pets but none have active subscriptions, still show them (with warnings)
        if (transformedPatients.length === 0 && !location.pathname.startsWith('/profile') && !location.pathname.startsWith('/pets') && !location.pathname.startsWith('/about') && !location.pathname.startsWith('/feedback') && !location.pathname.startsWith('/no-pets')) {
          navigate('/no-pets', { replace: true });
        }
        // If we have patients (even without active subscriptions) and we're on no-pets page, redirect to dashboard
        else if (transformedPatients.length > 0 && location.pathname.startsWith('/no-pets')) {
          navigate('/dashboard', { replace: true });
        }
        
        // Handle pet selection: check if currentPetId is valid, and select appropriate pet
        if (transformedPatients.length > 0) {
          const currentPet = currentPetId ? transformedPatients.find(p => p.id === currentPetId) : null;
          
          // If currentPetId doesn't exist in the list, or no pet is selected, select the first available pet
          if (!currentPetId || !currentPet) {
            // Prefer pets with active subscriptions, but fall back to any pet if none have active subscriptions
            const petToSelect = transformedPatients.find(p => p.ownerHasActiveSubscription) || transformedPatients[0];
            if (petToSelect) {
              setCurrentPetId(petToSelect.id);
              setCurrentPetName(petToSelect.name);
              localStorage.setItem('currentPetId', petToSelect.id);
              localStorage.setItem('currentPetName', petToSelect.name);
              
              // Dispatch custom event to notify other components
              window.dispatchEvent(new CustomEvent('petChanged', { 
                detail: { petId: petToSelect.id, petName: petToSelect.name } 
              }));
              
              queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
            }
          }
          // If currentPetId exists but pet name is missing, update it
          else if (currentPet && !currentPetName) {
            setCurrentPetName(currentPet.name);
            localStorage.setItem('currentPetName', currentPet.name);
          }
          // If current pet is disabled and there's an enabled pet available, switch to enabled pet
          else if (currentPet && !currentPet.ownerHasActiveSubscription) {
            const enabledPet = transformedPatients.find(p => p.ownerHasActiveSubscription);
            if (enabledPet && enabledPet.id !== currentPetId) {
              setCurrentPetId(enabledPet.id);
              setCurrentPetName(enabledPet.name);
              localStorage.setItem('currentPetId', enabledPet.id);
              localStorage.setItem('currentPetName', enabledPet.name);
              
              // Dispatch custom event to notify other components
              window.dispatchEvent(new CustomEvent('petChanged', { 
                detail: { petId: enabledPet.id, petName: enabledPet.name } 
              }));
              
              queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch patients:', error);
        setPatients([]);
        // If error and not on profile, pets, about, feedback, or no-pets page, redirect to no-pets
        if (!location.pathname.startsWith('/profile') && !location.pathname.startsWith('/pets') && !location.pathname.startsWith('/about') && !location.pathname.startsWith('/feedback') && !location.pathname.startsWith('/no-pets')) {
          navigate('/no-pets', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, [currentPetId, currentPetName, location.pathname, navigate, queryClient]);

  const handlePetChange = (event) => {
    const selectedPetId = event.target.value;
    const selectedPet = patients.find(p => p.id === selectedPetId);
    
    // Prevent selection of disabled pets
    if (!selectedPet || !selectedPet.ownerHasActiveSubscription) {
      // If trying to select a disabled pet, revert to current selection or find first enabled pet
      const currentPet = patients.find(p => p.id === currentPetId);
      if (currentPet && currentPet.ownerHasActiveSubscription) {
        // Keep current selection if it's enabled
        return;
      }
      // Otherwise, find first enabled pet
      const enabledPet = patients.find(p => p.ownerHasActiveSubscription);
      if (enabledPet) {
        setCurrentPetId(enabledPet.id);
        setCurrentPetName(enabledPet.name);
        localStorage.setItem('currentPetId', enabledPet.id);
        localStorage.setItem('currentPetName', enabledPet.name);
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('petChanged', { 
          detail: { petId: enabledPet.id, petName: enabledPet.name } 
        }));

        // Immediately refresh all active queries across the app
        queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
      }
      return;
    }
    
    // Only proceed if pet has active subscription
    setCurrentPetId(selectedPetId);
    setCurrentPetName(selectedPet.name);
    localStorage.setItem('currentPetId', selectedPetId);
    localStorage.setItem('currentPetName', selectedPet.name);
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('petChanged', { 
      detail: { petId: selectedPetId, petName: selectedPet.name } 
    }));

    // Immediately refresh all active queries across the app
    queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: AppStyles.colors.primary.dark,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'auto',
        pt: { xs: 2, md: 0 },
        '&::-webkit-scrollbar': {
          width: '0px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'transparent',
        },
      }}
    >
      {/* Logo and Title Section */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '100%',
          py: 4,
          pt: { xs: 8, md: 6 },
          px: 2,
          position: 'relative',
          borderBottom: `1px solid rgba(255,255,255,0.2)`,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mb: 3,
            cursor: 'pointer',
            display: 'inline-block',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
          onClick={() => navigate('/dashboard')}
        >
          <img
            src="/images/logo/textAndIconColorLightText.png"
            alt="Pawwi Logo"
            style={{
              height: '40px',
              width: 'auto',
              maxWidth: '220px',
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3)) brightness(1.1)',
            }}
          />
        </Box>
        
        <Box sx={{ textAlign: 'center', position: 'relative' }}>
          <Typography 
            variant="h6" 
            component="div"
            sx={{
              color: 'white',
              fontWeight: AppStyles.typography.fontWeight.bold,
              fontSize: '1.4rem',
              letterSpacing: '1px',
              // textTransform: 'uppercase',
              marginBottom: '4px',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
{t('layout.vetPortal')}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, px: 2, py: 2, position: 'relative', zIndex: 1 }}>
        <List sx={{ mb: 3 }}>
          {menuItems.map((item) => {
            // Check if there are any pets with active subscriptions
            const enabledPets = patients.filter(p => p.ownerHasActiveSubscription);
            const hasNoPets = !loading && enabledPets.length === 0;
            return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                disabled={hasNoPets}
                onClick={() => {
                  if (!hasNoPets) {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: hasNoPets ? 0.5 : 1,
                  cursor: hasNoPets ? 'not-allowed' : 'pointer',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                    '&:hover': {
                      background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                      transform: 'translateY(-3px)',
                    },
                  },
                  '&:hover': {
                    background: hasNoPets ? 'transparent' : 'rgba(255,255,255,0.1)',
                    transform: hasNoPets ? 'none' : 'translateY(-1px)',
                    boxShadow: hasNoPets ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                    transition: 'all 0.3s ease',
                    filter: location.pathname === item.path ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.9)',
                    fontWeight: location.pathname === item.path ? AppStyles.typography.fontWeight.bold : AppStyles.typography.fontWeight.medium,
                    textShadow: location.pathname === item.path ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>
            );
          })}
        </List>

        {/* Separator with enhanced styling */}
        <Divider 
          sx={{ 
            my: 3, 
            borderColor: 'rgba(255,255,255,0.3)', 
            opacity: 0.8,
            '&::before, &::after': {
              content: '""',
              flex: 1,
              borderBottom: '1px solid rgba(255,255,255,0.2)',
            },
          }} 
        />

        {/* Profile and Logout buttons */}
        <Box sx={{ mt: 'auto', px: 2, py: -3 }}>
          <List sx={{ mb: 0 }}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === '/profile'}
                onClick={() => {
                  navigate('/profile');
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                    '&:hover': {
                      background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                      transform: 'translateY(-3px)',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: location.pathname === '/profile' ? 'white' : 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                    transition: 'all 0.3s ease',
                    filter: location.pathname === '/profile' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                  }}
                >
                  <AccountIcon sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('layout.profile')}
                  sx={{
                    color: location.pathname === '/profile' ? 'white' : 'rgba(255,255,255,0.9)',
                    fontWeight: location.pathname === '/profile' ? AppStyles.typography.fontWeight.bold : AppStyles.typography.fontWeight.medium,
                    textShadow: location.pathname === '/profile' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === '/pets'}
                onClick={() => {
                  navigate('/pets');
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                    '&:hover': {
                      background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                      transform: 'translateY(-3px)',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: location.pathname === '/pets' ? 'white' : 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                    transition: 'all 0.3s ease',
                    filter: location.pathname === '/pets' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                  }}
                >
                  <PetsIcon sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('layout.pets')}
                  sx={{
                    color: location.pathname === '/pets' ? 'white' : 'rgba(255,255,255,0.9)',
                    fontWeight: location.pathname === '/pets' ? AppStyles.typography.fontWeight.bold : AppStyles.typography.fontWeight.medium,
                    textShadow: location.pathname === '/pets' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === '/about'}
                onClick={() => {
                  navigate('/about');
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                    '&:hover': {
                      background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                      transform: 'translateY(-3px)',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: location.pathname === '/about' ? 'white' : 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                    transition: 'all 0.3s ease',
                    filter: location.pathname === '/about' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                  }}
                >
                  <AboutIcon sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('layout.about')}
                  sx={{
                    color: location.pathname === '/about' ? 'white' : 'rgba(255,255,255,0.9)',
                    fontWeight: location.pathname === '/about' ? AppStyles.typography.fontWeight.bold : AppStyles.typography.fontWeight.medium,
                    textShadow: location.pathname === '/about' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === '/feedback'}
                onClick={() => {
                  navigate('/feedback');
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                    '&:hover': {
                      background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                      transform: 'translateY(-3px)',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: location.pathname === '/feedback' ? 'white' : 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                    transition: 'all 0.3s ease',
                    filter: location.pathname === '/feedback' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                  }}
                >
                  <FeedbackIcon sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('layout.feedback')}
                  sx={{
                    color: location.pathname === '/feedback' ? 'white' : 'rgba(255,255,255,0.9)',
                    fontWeight: location.pathname === '/feedback' ? AppStyles.typography.fontWeight.bold : AppStyles.typography.fontWeight.medium,
                    textShadow: location.pathname === '/feedback' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    minWidth: 40,
                  }}
                >
                  <LogoutIcon sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('layout.logout')}
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: AppStyles.typography.fontWeight.medium,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Box>

      {/* Bottom decorative element */}
      <Box
        sx={{
          height: '60px',
          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          zIndex: 1,
        }}
      />
    </Box>
  );

  const getPageTitle = () => {
    const p = location.pathname || '';
    if (p.startsWith('/dashboard')) return t('titles.dashboard');
    if (p.startsWith('/lab-results')) return t('titles.labResults');
    if (p.startsWith('/medication')) return t('titles.medication');
    if (p.startsWith('/nutrition')) return t('titles.nutrition');
    if (p.startsWith('/seizures')) return t('titles.seizures');
    if (p.startsWith('/vaccination')) return t('titles.vaccination');
    if (p.startsWith('/patients')) return t('titles.patients');
    if (p.startsWith('/profile')) return t('titles.profile');
    if (p.startsWith('/pets')) return t('titles.pets');
    if (p.startsWith('/about')) return t('titles.about');
    if (p.startsWith('/feedback')) return t('titles.feedback');
    if (p.startsWith('/no-pets')) return t('noPets.title');
    return '';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'flex', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: AppStyles.colors.primary.dark,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {getPageTitle()}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: { md: drawerWidth }, 
          flexShrink: { md: 0 },
          display: { xs: 'none', md: 'block' }
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '64px', md: 0 },
        }}
      >
        {/* Top Header with Pet Dropdown */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: { xs: 2, md: 3 },
            gap: { xs: 2, md: 0 },
            position: 'relative',
          }}
        >
          {/* Page Title */}
          <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' }, display: { xs: 'none', md: 'block' } }}>
            <Typography 
              variant="h3"
              sx={{
                color: AppStyles.colors.primary.dark,
                fontWeight: AppStyles.typography.fontWeight.bold,
                position: 'relative',
                display: 'inline-block',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: { xs: '60px', md: '80px' },
                  height: '3px',
                  background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  borderRadius: 2,
                }
              }}
            >
              {getPageTitle()}
            </Typography>
          </Box>
          
          {/* Pet Dropdown (hidden on Vet Profile, Pets, and NoPets routes) */}
          {!(location.pathname || '').startsWith('/profile') && !(location.pathname || '').startsWith('/pets') && !(location.pathname || '').startsWith('/about') && !(location.pathname || '').startsWith('/feedback') && !(location.pathname || '').startsWith('/no-pets') && (
          <Box sx={{ minWidth: { xs: '100%', md: 280 }, width: { xs: '100%', md: 'auto' } }}>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{
                  color: AppStyles.colors.primary.main,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  '&.Mui-focused': {
                    color: AppStyles.colors.primary.dark,
                  },
                }}
              >
{t('layout.petProfile')}
              </InputLabel>
              <Select
                value={(() => {
                  // Ensure currentPetId exists in the patients array to prevent MUI warning
                  const selectedPet = currentPetId ? patients.find(p => p.id === currentPetId) : null;
                  if (!selectedPet) {
                    // Invalid pet ID - return empty to prevent MUI warning
                    return '';
                  }
                  return currentPetId || '';
                })()}
                onChange={(event) => {
                  const selectedPetId = event.target.value;
                  const selectedPet = patients.find(p => p.id === selectedPetId);
                  
                  // Only allow selection if pet has active subscription
                  if (selectedPet && selectedPet.ownerHasActiveSubscription) {
                    handlePetChange(event);
                  }
                  // If trying to select a disabled pet, just don't update (MenuItem disabled prop will prevent it anyway)
                }}
                label={t('layout.petProfile')}
                displayEmpty
                disabled={loading}
                renderValue={(value) => {
                  if (!value) return <span style={{ color: '#999' }}>{t('layout.selectPet')}</span>;
                  const selectedPet = patients.find(p => p.id === value);
                  if (!selectedPet) return <span style={{ color: '#999' }}>{t('layout.selectPet')}</span>;
                  const isDisabled = !selectedPet.ownerHasActiveSubscription;
                  
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box>
                        <Typography sx={{ 
                          fontWeight: 600, 
                          color: isDisabled ? '#999' : AppStyles.colors.primary.dark,
                          fontSize: '0.9rem',
                          lineHeight: 1.2,
                        }}>
                          {selectedPet.name}{selectedPet.ownerName ? ` (${selectedPet.ownerName})` : ''}
                        </Typography>
                        {selectedPet.species && (
                          <Typography sx={{ 
                            fontSize: '0.7rem', 
                            color: isDisabled ? '#999' : AppStyles.colors.primary.main,
                            fontWeight: 500,
                            textTransform: 'capitalize',
                          }}>
                            {selectedPet.species}
                          </Typography>
                        )}
                        {isDisabled && (
                          <Typography sx={{ 
                            fontSize: '0.65rem', 
                            color: '#d32f2f',
                            fontStyle: 'italic',
                            mt: 0.25,
                          }}>
                            {t('layout.petRequiresSubscription')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                sx={{
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: 3,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent !important',
                    borderWidth: '0px !important',
                    border: '0 !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent !important',
                    borderWidth: '0px !important',
                    border: '0 !important',
                    boxShadow: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${AppStyles.colors.primary.dark} !important`,
                    borderWidth: '1px !important',
                    border: '1px solid',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      mt: 1,
                      '& .MuiMenuItem-root': {
                        borderRadius: 2,
                        margin: '4px 8px',
                        padding: '12px 16px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(188, 185, 118, 0.15)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
                          backgroundColor: 'rgba(188, 185, 118, 0.20) !important',
                          '&:hover': {
                            backgroundColor: 'rgba(188, 185, 118, 0.25) !important',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {patients.map((pet) => {
                  const isSelected = pet.id === currentPetId;
                  const isDisabled = !pet.ownerHasActiveSubscription;
                  return (
                    <MenuItem 
                      key={pet.id} 
                      value={pet.id}
                      disabled={isDisabled}
                      onClick={(e) => {
                        // Prevent selection of disabled pets
                        if (isDisabled) {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }
                      }}
                      sx={{
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        pointerEvents: isDisabled ? 'none' : 'auto',
                        '&.Mui-disabled': {
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%',
                        gap: 1.5
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isDisabled 
                                ? '#999' 
                                : isSelected 
                                  ? AppStyles.colors.primary.dark 
                                  : '#333',
                              fontSize: '0.95rem',
                              lineHeight: 1.2,
                            }}
                          >
                            {pet.name}{pet.ownerName ? ` (${pet.ownerName})` : ''}
                          </Typography>
                          {pet.species && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: isDisabled 
                                  ? '#999' 
                                  : isSelected 
                                    ? AppStyles.colors.primary.main 
                                    : '#666',
                                fontWeight: isSelected ? 600 : 400,
                                textTransform: 'capitalize',
                              }}
                            >
                              {pet.species}
                            </Typography>
                          )}
                          {isDisabled && (
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: '#d32f2f',
                                fontStyle: 'italic',
                                mt: 0.5,
                              }}
                            >
                              {t('layout.petRequiresSubscription')}
                            </Typography>
                          )}
                        </Box>
                        {isSelected && !isDisabled && (
                          <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: AppStyles.colors.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.8rem',
                          }}>
                            ✓
                          </Box>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
                {!currentPetId && (
                  <MenuItem value="" disabled>
                    <Typography color="textSecondary">
                      {loading ? t('layout.loadingPets') : t('layout.selectPet')}
                    </Typography>
                  </MenuItem>
                )}
                {patients.length === 0 && !loading && (
                  <MenuItem disabled>
                    <Typography color="textSecondary">{t('layout.noPetsAvailable')}</Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
          )}
        </Box>
        
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
